<template>
  <div class="launcher">
    <header class="home-header">
      <div class="home-logo"><img src="../assets/aslogo.png" alt="logo"></div>
      <div>
        <p class="home-date">{{ today }}</p>
        <h1 class="home-title">{{ greeting }}{{ name ? `, ${name}` : '' }}</h1>
      </div>
      <div class="home-actions">
        <b-button v-if="!editing" variant="secondary" size="sm" @click="startEdit">
          <b-icon-sliders class="mr-1"></b-icon-sliders>Customize
        </b-button>
        <template v-else>
          <b-button v-if="custom" variant="link" size="sm" class="text-muted" @click="reset">Reset to default</b-button>
          <b-button variant="secondary" size="sm" @click="editing = false">Cancel</b-button>
          <b-button variant="primary" size="sm" @click="save">Done</b-button>
        </template>
      </div>
    </header>

    <p v-if="editing" class="home-edit-hint">
      Remove items you don't need, add them back below each group, and drag or use the arrows to reorder within a group.
    </p>

    <!-- Editing: every group, its shown items (arrangeable) and hidden ones (addable) -->
    <template v-if="editing">
      <section v-for="group in draftGroups"
               :key="group.title"
               :class="['home-section', `tone-${group.tone}`, sectionClass(group)]">
        <h2 class="home-section-title">{{ group.title }}</h2>
        <div v-if="group.shown.length" class="home-grid home-grid-edit">
          <div v-for="(item, j) in group.shown"
               :key="item.to"
               :class="['home-tile', 'home-tile-edit', itemClass(item), { dragging: dragPath === item.to }]"
               draggable="true"
               @dragstart="dragPath = item.to"
               @dragover="canDrop(item) && $event.preventDefault()"
               @drop="dropOn(item.to)"
               @dragend="dragPath = null">
            <b-icon-grip-vertical class="home-grip"></b-icon-grip-vertical>
            <span class="home-tile-icon"><b-icon :icon="item.icon"></b-icon></span>
            <span class="home-tile-label">{{ item.label }}</span>
            <button class="home-tile-btn" :disabled="j === 0" aria-label="Move earlier" @click="move(item.to, -1)">
              <b-icon-arrow-left></b-icon-arrow-left>
            </button>
            <button class="home-tile-btn" :disabled="j === group.shown.length - 1" aria-label="Move later" @click="move(item.to, 1)">
              <b-icon-arrow-right></b-icon-arrow-right>
            </button>
            <button class="home-tile-btn remove" aria-label="Remove from home" @click="remove(item.to)">
              <b-icon-x></b-icon-x>
            </button>
          </div>
        </div>
        <div v-else class="home-drop-empty">This group is hidden from your home. Add items below to show it.</div>
        <div v-if="group.hidden.length" class="home-grid home-grid-add">
          <button v-for="item in group.hidden"
                  :key="item.to"
                  :class="['home-tile', 'home-tile-add', itemClass(item)]"
                  @click="draft.push(item.to)">
            <span class="home-tile-icon"><b-icon :icon="item.icon"></b-icon></span>
            <span class="home-tile-label">{{ item.label }}</span>
            <b-icon-plus-circle class="home-tile-chevron"></b-icon-plus-circle>
          </button>
        </div>
      </section>
    </template>

    <!-- Personal layout: same groups, only the chosen items; empty groups hidden -->
    <template v-else-if="custom">
      <section v-for="group in pinnedGroups"
               :key="group.title"
               :class="['home-section', `tone-${group.tone}`, sectionClass({ items: group.shown })]">
        <h2 class="home-section-title">{{ group.title }}</h2>
        <div class="home-grid">
          <router-link v-for="item in group.shown"
                       :key="item.to"
                       :to="item.to"
                       :class="['home-tile', itemClass(item)]">
            <span class="home-tile-icon"><b-icon :icon="item.icon"></b-icon></span>
            <span class="home-tile-label">{{ item.label }}</span>
            <b-icon icon="chevron-right" class="home-tile-chevron"></b-icon>
          </router-link>
        </div>
      </section>
      <div v-if="!pinnedGroups.length" class="empty-state">
        <b-icon-grid></b-icon-grid>
        Your home is empty. Use Customize to add items, or the sidebar to get around.
      </div>
    </template>

    <!-- Default: everything, grouped like the sidebar -->
    <template v-else>
      <section v-for="section in menu" :key="section.title" :class="['home-section', `tone-${section.tone}`, sectionClass(section)]">
        <h2 class="home-section-title">{{ section.title }}</h2>
        <div class="home-grid">
          <router-link v-for="item in section.items"
                       :key="item.to"
                       :to="item.to"
                       :class="['home-tile', itemClass(item)]">
            <span class="home-tile-icon"><b-icon :icon="item.icon"></b-icon></span>
            <span class="home-tile-label">{{ item.label }}</span>
            <b-icon icon="chevron-right" class="home-tile-chevron"></b-icon>
          </router-link>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
import { menuFor, itemClass, sectionClass } from '@/navigation';

// The personal layout is an ordered list of menu paths, kept per user in
// this browser. It is always filtered through menuFor(), so it can never
// show something the user's role doesn't allow, and it is always shown in
// the menu's groups: the list only decides which items show and their order
// within their own group. ponytail: browser-only, move to a user setting on
// the server if layouts should follow people across devices.
const storageKey = (user) => `homeItems:${(user && user.username) || ''}`;

function readPinned(user) {
  try {
    const saved = JSON.parse(localStorage.getItem(storageKey(user)));
    return Array.isArray(saved) ? saved : null;
  } catch (e) {
    return null;
  }
}

export default {
  name: 'Home',
  data() {
    const user = JSON.parse(localStorage.getItem('user'));
    return {
      user,
      pinned: readPinned(user), // null = default layout
      editing: false,
      draft: [],
      dragPath: null,
    };
  },
  computed: {
    menu() {
      return menuFor(this.user);
    },
    sectionOf() {
      return Object.fromEntries(this.menu.flatMap((s) => s.items.map((i) => [i.to, s.title])));
    },
    custom() {
      return this.pinned !== null;
    },
    pinnedGroups() {
      return this.groupsFor(this.pinned || []).filter((g) => g.shown.length);
    },
    draftGroups() {
      return this.groupsFor(this.draft);
    },
    name() {
      const n = this.user && this.user.username;
      return n ? n.charAt(0).toUpperCase() + n.slice(1) : '';
    },
    greeting() {
      const h = new Date().getHours();
      if (h < 12) return 'Good morning';
      if (h < 18) return 'Good afternoon';
      return 'Good evening';
    },
    today() {
      return new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
    },
  },
  methods: {
    itemClass,
    sectionClass,
    // The menu's groups, in menu order, each with the listed items in list
    // order (shown) and the rest of the group (hidden). Unknown or no longer
    // permitted paths simply drop out.
    groupsFor(paths) {
      const listed = new Set(paths);
      return this.menu.map((s) => ({
        ...s,
        shown: paths.map((p) => s.items.find((i) => i.to === p)).filter(Boolean),
        hidden: s.items.filter((i) => !listed.has(i.to)),
      }));
    },
    startEdit() {
      // Start from what's on screen now: the personal layout or everything.
      this.draft = this.custom
        ? this.pinnedGroups.flatMap((g) => g.shown.map((i) => i.to))
        : this.menu.flatMap((s) => s.items.map((i) => i.to));
      this.editing = true;
    },
    // Look items up by path, not render index, so quick repeated clicks
    // before a re-render never act on the wrong tile.
    remove(to) {
      this.draft = this.draft.filter((p) => p !== to);
    },
    // Swap with the previous/next item of the same group; other groups'
    // items in between are left where they are.
    move(to, delta) {
      const siblings = this.draft.filter((p) => this.sectionOf[p] === this.sectionOf[to]);
      const other = siblings[siblings.indexOf(to) + delta];
      if (!other) return;
      const a = this.draft.indexOf(to);
      const b = this.draft.indexOf(other);
      this.$set(this.draft, a, other);
      this.$set(this.draft, b, to);
    },
    canDrop(item) {
      return !!this.dragPath && this.sectionOf[this.dragPath] === this.sectionOf[item.to];
    },
    // Dropping on another tile of the same group takes that tile's place.
    dropOn(target) {
      const from = this.dragPath;
      this.dragPath = null;
      if (!from || from === target || this.sectionOf[from] !== this.sectionOf[target]) return;
      const fromIdx = this.draft.indexOf(from);
      const targetIdx = this.draft.indexOf(target);
      this.draft.splice(fromIdx, 1);
      this.draft.splice(fromIdx < targetIdx ? this.draft.indexOf(target) + 1 : this.draft.indexOf(target), 0, from);
    },
    save() {
      this.pinned = this.draftGroups.flatMap((g) => g.shown.map((i) => i.to));
      this.persist(JSON.stringify(this.pinned));
      this.editing = false;
    },
    reset() {
      this.pinned = null;
      this.persist(null);
      this.editing = false;
    },
    persist(value) {
      try {
        if (value === null) localStorage.removeItem(storageKey(this.user));
        else localStorage.setItem(storageKey(this.user), value);
      } catch (e) {
        this.$toast.warning('Could not save your home layout in this browser');
      }
    },
  },
};
</script>

<style>
.launcher {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.5rem 0 2rem;
  /* Soft pastel glow behind the greeting to lift the page off pure black */
  background:
    /* each ellipse fades out fully inside the box, so no hard edges */
    radial-gradient(300px 140px at 22% 150px, rgba(142, 197, 255, 0.13), transparent),
    radial-gradient(280px 130px at 55% 140px, rgba(195, 166, 255, 0.1), transparent),
    radial-gradient(260px 130px at 80% 150px, rgba(255, 158, 187, 0.08), transparent);
  background-repeat: no-repeat;
}

.home-header {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 1.1rem;
  margin-bottom: 2rem;
}

.home-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: auto;
}

.home-logo {
  display: inline-flex;
  padding: 12px;
  border-radius: 18px;
  background: linear-gradient(160deg, var(--surface-3), var(--surface));
  border: 1px solid var(--border-strong);
  box-shadow: var(--shadow);
}

.home-logo img {
  width: 44px;
  height: auto;
}

.home-date {
  margin: 0;
  color: var(--text-faint);
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.home-title {
  margin: 0;
  font-size: clamp(1.5rem, 3vw, 2rem);
  font-weight: 750;
  letter-spacing: -0.025em;
}

.home-section + .home-section {
  margin-top: 1.75rem;
}

.home-section-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--tone, var(--text-faint));
  margin: 0 0 0.6rem 0.25rem;
}

.home-section[class*="tone-"] > .home-section-title::before {
  content: '';
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--tone);
  box-shadow: 0 0 10px rgba(var(--tone-rgb), 0.6);
}

.home-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 0.6rem;
}

.home-tile {
  align-items: center;
  gap: 0.8rem;
  padding: 0.85rem 0.9rem;
  background:
    linear-gradient(135deg, rgba(var(--tone-rgb, 255, 255, 255), 0.15), rgba(var(--tone-rgb, 255, 255, 255), 0.05) 65%),
    var(--surface-2);
  border: 1px solid rgba(var(--tone-rgb, 255, 255, 255), 0.2);
  border-radius: var(--radius);
  color: var(--text) !important;
  text-decoration: none !important;
  text-align: left;
  box-shadow: var(--shadow-sm);
  transition: transform 0.15s ease, background-color 0.15s ease, border-color 0.15s ease, opacity 0.15s ease;
}

.home-tile:hover {
  background:
    linear-gradient(135deg, rgba(var(--tone-rgb, 255, 255, 255), 0.24), rgba(var(--tone-rgb, 255, 255, 255), 0.08) 65%),
    var(--surface-2);
  border-color: rgba(var(--tone-rgb, 255, 255, 255), 0.35);
  transform: translateY(-1px);
}

.home-tile:active {
  transform: scale(0.99);
}

.home-tile-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  border-radius: 10px;
  background: rgba(var(--tone-rgb, 10, 132, 255), 0.24);
  color: var(--tone, var(--accent));
  font-size: 1.1rem;
}

.home-tile-label {
  flex: 1;
  min-width: 0;
  font-weight: 600;
  font-size: 0.925rem;
}

.home-tile-chevron {
  color: var(--text-faint);
  font-size: 0.85rem;
}

.home-tile:hover .home-tile-chevron {
  color: var(--tone, var(--text-muted));
}

/* ---- Customize mode ---- */
/* Edit tiles carry three extra buttons, so give them wider columns
   (a single column on phones) instead of letting them overflow. */
.home-grid-edit {
  grid-template-columns: repeat(auto-fill, minmax(min(300px, 100%), 1fr));
}

.home-tile-edit {
  gap: 0.5rem;
  padding-left: 0.5rem;
  cursor: grab;
  border-style: dashed;
}

.home-tile-edit:hover {
  transform: none;
}

.home-tile-edit.dragging {
  opacity: 0.4;
}

.home-grip {
  color: var(--text-faint);
  flex-shrink: 0;
}

.home-tile-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--text-muted);
}

.home-tile-btn:hover:not(:disabled) {
  background: var(--hover);
  color: var(--text);
}

.home-tile-btn:disabled {
  opacity: 0.25;
}

.home-tile-btn.remove:hover {
  background: rgba(229, 72, 77, 0.18);
  color: var(--danger);
}

.home-tile-add {
  opacity: 0.7;
}

.home-tile-add:hover {
  opacity: 1;
}

.home-tile-add .home-tile-chevron {
  color: var(--tone, var(--accent));
  font-size: 1rem;
}

.home-drop-empty {
  padding: 1rem;
  border: 1px dashed var(--border-strong);
  border-radius: var(--radius);
  color: var(--text-muted);
  font-size: 0.875rem;
  text-align: center;
}

.home-edit-hint {
  margin: -1rem 0 1.5rem;
  color: var(--text-muted);
  font-size: 0.875rem;
}

/* Hidden items of a group, shown under its arranged items while editing */
.home-grid-add {
  margin-top: 0.6rem;
}
</style>
